import { DatePipe, DecimalPipe } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  ErrorHandler,
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { ChartModule } from "primeng/chart";
import { environment } from "src/environments/environment";

import { AppComponent } from "./app.component";
import { AppInitializer } from "./app.initializer";
import { AppRoutingModule } from "./app-routing.module";
import { InspectionModule } from "./inspection/inspection.module";
import { AuthInterceptor } from "./inspection/interceptors/auth.interceptor";
import { WebInterceptor } from "./inspection/interceptors/web.interceptor";
import { localeFactory } from "./locale-factory";
import { OPEN_NEW_INSPECTION_RETRY_CONFIGURATION } from "./session/open-new-inspection/open-new-inspection.component";
import { MAX_IDLE_TIME_IN_SECONDS } from "./session/services/idle-time/idle-time.service";
import { ErrorHandlerService } from "./shared/logging/error-handler.service";
import { ErrorLoggerService } from "./shared/logging/error-logger.service";
import { LoggingService } from "./shared/logging/logging.service";

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    ChartModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    InspectionModule,
  ],
  providers: [
    provideZoneChangeDetection(),
    LoggingService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WebInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useFactory: localeFactory },
    { provide: MAX_IDLE_TIME_IN_SECONDS, useValue: environment.maxIdleTime },
    {
      provide: OPEN_NEW_INSPECTION_RETRY_CONFIGURATION,
      useValue: { milliSecondsBetweenAttempts: 5000, numberOfAttempts: 6 },
    },
    DecimalPipe,
    DatePipe,
    provideAppInitializer(() => {
      const initializerFn = AppInitializerFactory(inject(AppInitializer));
      return initializerFn();
    }),
    { provide: ErrorHandler, useClass: ErrorLoggerService },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
      deps: [LoggingService],
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: "./assets/i18n/",
        suffix: ".json",
      }),
    }),
  ],
})
export class AppModule {}

export function AppInitializerFactory(appInitializer: AppInitializer) {
  return () => appInitializer.init();
}
